import { useCallback, useMemo } from "react";

import {
  useArchiveSkillCollectionVersion,
  useCreateSkillCollectionLabel,
  useDeleteSkillCollectionLabel,
  useListSkillCollectionVersions,
  useMoveSkillCollectionLabel,
} from "@/queries/skill-collections";
import type { SkillCollectionVersionRecord } from "@/queries/skill-collections";
import type { VersionSelectorVersionId } from "@/components/VersionSelector/types";
import { displayErrorToast } from "@/utils/toast";

interface UseVersionLabelActionsParams {
  workspaceId: number;
  collectionId: string;
}

export const useVersionLabelActions = ({
  workspaceId,
  collectionId,
}: UseVersionLabelActionsParams) => {
  const versionsQuery = useListSkillCollectionVersions(workspaceId, collectionId);
  const createLabel = useCreateSkillCollectionLabel();
  const moveLabel = useMoveSkillCollectionLabel();
  const deleteLabel = useDeleteSkillCollectionLabel();
  const archiveSkillCollectionVersion = useArchiveSkillCollectionVersion();

  const versions = useMemo<SkillCollectionVersionRecord[]>(
    () => versionsQuery.data?.pages.flatMap(page => page.versions) ?? [],
    [versionsQuery.data?.pages],
  );

  const navigateToVersionLink = useCallback(
    (versionNumber: number) =>
      `/workspace/${workspaceId}/skill-collection/${collectionId}/version/${versionNumber}`,
    [workspaceId, collectionId],
  );

  const handleAddReleaseLabel = useCallback(
    async (label: string, versionId: VersionSelectorVersionId) => {
      const existing = versions
        .find(v => v.labels?.some(l => l.name === label))
        ?.labels?.find(l => l.name === label);

      if (existing) {
        return () =>
          moveLabel.mutateAsync({
            workspaceId,
            collectionId,
            labelId: existing.id,
            targetVersionId: String(versionId),
          });
      }

      return createLabel.mutateAsync({
        workspaceId,
        collectionId,
        name: label,
        versionId: String(versionId),
      });
    },
    [versions, workspaceId, collectionId, createLabel, moveLabel],
  );

  const handleMoveReleaseLabel = useCallback(
    async (labelId: number, _labelName: string, targetVersionId: number | string) => {
      const targetVersionIdStr = String(targetVersionId);
      try {
        await moveLabel.mutateAsync({
          workspaceId,
          collectionId,
          labelId,
          targetVersionId: targetVersionIdStr,
        });
      } catch (error) {
        displayErrorToast(error instanceof Error ? error.message : "Failed to move label");
      }
    },
    [workspaceId, collectionId, moveLabel],
  );

  const handleRemoveReleaseLabel = useCallback(
    async (labelId: number) => {
      try {
        await deleteLabel.mutateAsync({
          workspaceId,
          collectionId,
          labelId,
        });
      } catch (error) {
        displayErrorToast(error instanceof Error ? error.message : "Failed to delete label");
      }
    },
    [workspaceId, collectionId, deleteLabel],
  );

  return {
    versions,
    versionsQuery,
    navigateToVersionLink,
    handleAddReleaseLabel,
    handleMoveReleaseLabel,
    handleRemoveReleaseLabel,
    archiveSkillCollectionVersion,
  };
};
